function decodeEntities(value) {
  if (typeof document === "undefined") return value;
  const textarea = document.createElement("textarea");
  textarea.innerHTML = value;
  return textarea.value;
}

function resolvePreviewImageSrc(value) {
  const raw = decodeEntities(String(value || "").trim());
  if (!raw) return "";

  if (/^data:image\//i.test(raw) || /^https?:\/\//i.test(raw)) {
    return raw;
  }

  const envURL = process.env.REACT_APP_API_URL?.trim();
  const configuredBase = envURL && envURL.startsWith("https://") ? envURL.replace(/\/+$/, "") : "";
  const runtimeBase = typeof window !== "undefined" ? window.location.origin.replace(/\/+$/, "") : "";
  const base = configuredBase || runtimeBase;

  if (raw.startsWith("/")) {
    return base ? `${base}${raw}` : raw;
  }

  if (raw.startsWith("uploads/")) {
    return base ? `${base}/${raw}` : `/${raw}`;
  }

  return raw;
}

function firstMeaningfulSentence(text) {
  const compact = text.replace(/\s+/g, " ").trim();
  if (!compact) return "";

  const sentences = compact
    .split(/(?<=[.!?])\s+/)
    .map((part) => part.trim())
    .filter(Boolean);

  const preferred = sentences.find((sentence) => {
    const lower = sentence.toLowerCase();
    return !lower.startsWith("html document:")
      && !lower.startsWith("doctype")
      && lower !== "[image]"
      && sentence.length > 24;
  });

  return preferred || sentences[0] || compact;
}

function stripDanglingTagFragments(text) {
  return String(text || "")
    .replace(/<\/?[a-z][a-z0-9:-]*[^>\s]*$/gi, " ")
    .replace(/<\/?[a-z][a-z0-9:-]*(?=\s|$)/gi, " ");
}

export function sanitizePreviewText(value) {
  const source = String(value || "");
  if (!source.trim()) return "";

  const cleaned = decodeEntities(
    source
      .replace(/<!doctype[^>]*>/gi, " ")
      .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, " ")
      .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, " ")
      .replace(/<!--[\s\S]*?-->/g, " ")
      .replace(/\[Image attachment\]/gi, " ")
      .replace(/<img\b[^>]*>?/gi, " ")
      .replace(/<\/?(figure|figcaption|div|section|article|header|footer|main|br|p|li|ul|ol|h[1-6])[^>]*>/gi, " ")
      .replace(/<a[^>]+href=["']([^"']+)["'][^>]*>([\s\S]*?)<\/a>/gi, " $2 ")
      .replace(/<[^>]+>/g, " ")
      .replace(/\bhttps?:\/\/[^\s<"]+/gi, " [Link] ")
      .replace(/\s+/g, " ")
      .trim()
  )
    .replace(/<img\b[^>]*>?/gi, " ")
    .replace(/<\s*img\b[^>]*$/i, " ")
    .replace(/<\/?[a-z][^>]*>/gi, " ")
    .replace(/\s+/g, " ")
    .trim();

  return stripDanglingTagFragments(cleaned)
    .replace(/\s+/g, " ")
    .trim();
}

export function buildExcerptPreview(value, maxLength = 180) {
  return sanitizePreviewText(value).slice(0, maxLength);
}

export function buildCardPreview(excerpt) {
  const source = String(excerpt || "");
  if (!source.trim()) {
    return { text: "", hasImage: false, imageSrc: "", links: [] };
  }

  const titleMatch = source.match(/<title[^>]*>([\s\S]*?)<\/title>/i);
  const docTitle = titleMatch ? decodeEntities(titleMatch[1].replace(/\s+/g, " ").trim()) : "";
  const hasImage = /<img\b/i.test(source) || /data:image\//i.test(source);
  const imageSrcMatch = source.match(/<img[^>]+src=["']([^"']+)["']/i);
  const imageSrc = imageSrcMatch?.[1] ? resolvePreviewImageSrc(imageSrcMatch[1]) : "";
  const links = Array.from(
    new Set(
      [
        ...source.matchAll(/<a[^>]+href=["']([^"']+)["']/gi),
        ...source.matchAll(/\bhttps?:\/\/[^\s<"]+/gi),
      ]
        .map((match) => match[1] || match[0] || "")
        .map((item) => String(item).trim())
        .filter((item) => /^https?:\/\//i.test(item))
    )
  ).slice(0, 2);

  const summaryText = firstMeaningfulSentence(sanitizePreviewText(source));
  const text = docTitle
    ? `HTML document: ${docTitle}${summaryText && !summaryText.includes(docTitle) ? ` - ${summaryText}` : ""}`
    : summaryText;

  return {
    text: text.slice(0, 220).trim(),
    hasImage,
    imageSrc,
    links,
  };
}
